// partners/adminView/pages/A8_AdminPayoutDetail.jsx

import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetAdminPayoutDetailQuery,
  useRetryPayoutMutation,
} from "../../../apis/adminApiSlice";
import AdminLayout from "./A0_AdminLayout";
import { formatUSD, formatUsdWithInr } from "../formatMoney";
import { capturePartnerEvent } from "../../../utils/analytics";

export default function AdminPayoutDetail() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const { data: payout, isLoading, refetch } = useGetAdminPayoutDetailQuery(id);
  const [retryPayout, { isLoading: retrying }] = useRetryPayoutMutation();
  const [feedback, setFeedback] = useState("");
  const viewedRef = useRef(false);

  useEffect(() => {
    if (payout && !viewedRef.current) {
      viewedRef.current = true;
      capturePartnerEvent("partner_admin_payout_detail_viewed", {
        payout_id: id,
        status: payout.status,
      });
    }
  }, [payout, id]);

  if (isLoading)
    return <AdminLayout><div className="text-mnf-text-muted text-sm">Loading...</div></AdminLayout>;
  if (!payout)
    return <AdminLayout><div className="text-mnf-error text-sm">Payout not found.</div></AdminLayout>;

  const p = payout;

  const handleRetry = async () => {
    if (!window.confirm("Retry this payout via RazorpayX?")) return;
    try {
      await retryPayout(id).unwrap();
      setFeedback("Retry initiated.");
      capturePartnerEvent("partner_admin_payout_retried", { payout_id: id, success: true });
      refetch();
    } catch (e) {
      capturePartnerEvent("partner_admin_payout_retried", { payout_id: id, success: false });
      setFeedback(`Retry failed: ${e?.data?.error || "unknown error"}`);
    }
  };

  const row = (label, value, cls) => (
    <div className="flex py-2.5 border-b border-white/[0.04]">
      <div className="w-48 text-xs text-mnf-text-muted shrink-0">{label}</div>
      <div className={`text-sm ${cls || "text-mnf-text-primary"}`}>{value ?? "—"}</div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="text-mnf-text-muted hover:text-mnf-cyan text-sm transition-colors"
        >
          ← Back
        </button>
        <h1 className="font-poppins text-xl font-bold">Payout Detail</h1>
      </div>

      <div className="max-w-xl bg-mnf-dark-2 border border-white/[0.08] rounded-mnf-lg p-6">
        {row("Partner",            p.partner_name)}
        {row("Period",             `${p.period_month}/${p.period_year}`)}
        {row("Gross amount (USD)", formatUsdWithInr(p.gross_amount, p.gross_amount_inr))}
        {row("TDS rate",           `${p.tds_rate}%`)}
        {row("TDS deducted (USD)", formatUSD(p.tds_amount), "text-mnf-error")}
        {row(
          "Net to transfer",
          formatUsdWithInr(p.net_amount, p.net_amount_inr),
          "text-mnf-success font-semibold"
        )}
        {p.conversion_rate != null &&
          row("FX rate (USD→INR)", String(p.conversion_rate))}
        {row("Payment method",     p.payment_method)}
        {row("Status",             p.status)}
        {row("RazorpayX payout ID", p.razorpay_payout_id || "Not initiated")}
        {row("UTR",                p.razorpay_utr || "Not available")}
        {row("Paid at",            p.paid_at ? new Date(p.paid_at).toLocaleString("en-IN") : "Not paid")}
        {p.failure_reason && row("Failure reason", p.failure_reason, "text-mnf-error")}

        <div className="mt-5 flex gap-3">
          {p.status === "failed" && (
            <button
              onClick={handleRetry}
              disabled={retrying}
              className="font-poppins text-xs font-semibold px-5 py-2.5 rounded-mnf-pill bg-mnf-gradient text-mnf-btn-text disabled:opacity-50 transition-opacity"
            >
              {retrying ? "Retrying…" : "Retry payout"}
            </button>
          )}
        </div>
        {feedback && <div className="mt-3 text-xs text-mnf-cyan">{feedback}</div>}
      </div>
    </AdminLayout>
  );
}
